import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, Mail, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeWelcomePage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState(() => (params.get('session_id') ? 'checking' : 'error'));
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const id = setTimeout(async function poll() {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setStatus('paid');
        } else if (pollCount < 12) {
          setPollCount((c) => c + 1);
          setTimeout(poll, 2500);
        } else {
          setStatus('pending');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    }, 0);
    return () => { cancelled = true; clearTimeout(id); };
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" data-testid="practice-welcome-root">
      <Card className="max-w-lg w-full p-8 bg-card border-silver/20 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-8 h-8 text-secondary mx-auto animate-spin mb-4" />
            <h1 className="font-heading text-2xl text-foreground mb-2">Confirming your purchase…</h1>
            <p className="text-sm text-muted-foreground">This usually takes about 10 seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-foreground mb-3" data-testid="practice-welcome-success">You&apos;re in. Welcome home.</h1>
            <p className="text-base text-foreground/85 leading-relaxed mb-5">
              Your <strong>Power Planning Practice App</strong> is unlocked for life. We just emailed your magic link — tap it to open your library on any device, any time.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
              <Mail className="w-4 h-4" /> Check your inbox (and spam folder).
            </div>
            <p className="text-xs text-muted-foreground mb-6">Didn&apos;t get the email? Use the email-recovery box on the landing page to resend.</p>
            <Link to="/practice">
              <Button variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary/10">Back to landing page</Button>
            </Link>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-foreground mb-2">Almost there…</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Payment confirmation is taking a moment. Your magic link will arrive in your inbox shortly. You can safely close this tab.
            </p>
            <Link to="/practice"><Button variant="outline">Back to landing page</Button></Link>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-foreground mb-2">Something went sideways</h1>
            <p className="text-sm text-muted-foreground mb-5">We couldn&apos;t confirm your payment. Please refresh or contact support.</p>
            <Link to="/practice"><Button>Try again</Button></Link>
          </>
        )}
      </Card>
    </div>
  );
}
